import { compose } from '@ngrx/store';
import { List, Map } from 'immutable';

import { EditableItem, EditableSection } from '®shared/reading-list/models';

import {
  ContainerEditActionTypes,
  ContainerEditActions,
  ContainerIdentifier,
  ContainerIdentifierWithIndex,
  EntryIdentifier,
} from './container-edit.actions';
import {
  containerKey,
  extractContainerStructure,
  getContainedTypeAndIdOnly,
} from './container-structure-utils';
import { ContainerActionTypes, ContainerActions } from './container.actions';
import { ContainedTypeAndId, ContainerStructure } from './container.state';

export function containerStructureReducer(
  state: ContainerStructure,
  action: ContainerActions | ContainerEditActions,
): ContainerStructure {
  switch (action.type) {
    case ContainerActionTypes.CONTAINER_INITIALISED: {
      const { container } = action.payload;
      return state.merge(extractContainerStructure(container));
    }

    case ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM: {
      const { id, to } = action.payload;
      return insertIntoContainer({ id, type: 'items' }, to)(state);
    }

    case ContainerEditActionTypes.CREATE_NOTE_ITEM: {
      const { id, to } = action.payload;
      return insertIntoContainer({ id, type: 'items' }, to)(state);
    }

    case ContainerEditActionTypes.CANCEL_CREATE_NOTE_ITEM: {
      const { id, to } = action.payload;
      return removeFromContainer({ id, type: 'items' }, to)(state);
    }

    case ContainerEditActionTypes.CONFIRM_DELETE_ITEM: {
      const { item, from } = action.payload;
      return removeFromContainer(item, from)(state);
    }

    case ContainerEditActionTypes.CANCEL_CREATE_SECTION: {
      const { id, to } = action.payload;
      const containedTypeAndId = getContainedTypeAndIdOnly({
        id,
        type: 'sections',
      });
      return removeFromContainer(containedTypeAndId, to)(state);
    }

    case ContainerEditActionTypes.CREATE_SECTION: {
      const { id, to } = action.payload;
      const containedTypeAndId = getContainedTypeAndIdOnly({
        id,
        type: 'sections',
      });
      return insertIntoContainer(containedTypeAndId, to)(state);
    }

    case ContainerEditActionTypes.CONFIRM_CREATE_SECTION: {
      const { id } = action.payload;
      const containedTypeAndId = getContainedTypeAndIdOnly({
        id,
        type: 'sections',
      });
      return insertAsContainer(containedTypeAndId)(state);
    }

    case ContainerEditActionTypes.CONFIRM_DELETE_SECTION: {
      const { section, from, descendantSections } = action.payload;
      const removeSection = compose(
        removeFromContainer(section, from),
        removeAsContainer(section),
        removeAllAsContainers(descendantSections),
      );
      return removeSection(state);
    }

    /* @todo: this will change when we introduce Copy */
    case ContainerEditActionTypes.PASTE_ITEM:
    case ContainerEditActionTypes.MOVE_ITEM: {
      const { item, from, to } = action.payload;
      return move(item, from, to)(state);
    }

    case ContainerEditActionTypes.PASTE_SECTION:
    case ContainerEditActionTypes.MOVE_SECTION: {
      const { section, from, to } = action.payload;
      return move(section, from, to)(state);
    }

    default: {
      return state;
    }
  }
}

/**
 * Return type will be a closure that applies a reduction
 */
type ReducerClosure = (
  structure: Map<string, List<ContainedTypeAndId>>,
) => Map<string, List<ContainedTypeAndId>>;

function removeFromContainer<T extends EditableItem | EditableSection>(
  entry: T,
  from: ContainerIdentifier,
): ReducerClosure {
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.update(containerKey(from), fromContainer =>
      !fromContainer
        ? fromContainer
        : fromContainer.filterNot(
            contained =>
              contained.type === entry.type && contained.id === entry.id,
          ),
    );
}

function move<T extends EditableItem | EditableSection>(
  entry: T,
  from: ContainerIdentifier,
  to: ContainerIdentifierWithIndex,
): ReducerClosure {
  if (containerKey(from) !== containerKey(to)) {
    return compose(
      removeFromContainer(entry, from),
      insertIntoContainer(entry, to),
    );
  }
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.update(containerKey(from), fromContainer => {
      if (!fromContainer) {
        return fromContainer;
      }

      const containedTypeAndId = getContainedTypeAndIdOnly(entry);
      const isEntry = other => containedTypeAndId.equals(other);

      const fromIndex = fromContainer.findIndex(isEntry);

      if (to.index !== 0 && !to.index) {
        // no target index - move to the end of the container
        return fromContainer.delete(fromIndex).push(containedTypeAndId);
      } else if (to.index === fromIndex) {
        // to/from are the same - no change needed
        return fromContainer;
      }
      // remove item and reinsert at the target index
      return fromContainer
        .delete(fromIndex)
        .insert(to.index, containedTypeAndId);
    });
}

function removeAsContainer(section: EntryIdentifier): ReducerClosure {
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.remove(containerKey(section));
}

function removeAllAsContainers(sectionIds: List<string>): ReducerClosure {
  const sectionKeys = sectionIds.map(id =>
    containerKey({ id, type: 'sections' }),
  );
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.removeAll(sectionKeys);
}

function insertAsContainer(section: ContainedTypeAndId): ReducerClosure {
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.merge({
      [containerKey(section)]: List<ContainedTypeAndId>(),
    });
}

function insertIntoContainer<
  T extends EditableItem | EditableSection | ContainedTypeAndId,
>(entry: T, to: ContainerIdentifierWithIndex): ReducerClosure {
  return (containerStructure: Map<string, List<ContainedTypeAndId>>) =>
    containerStructure.update(
      containerKey(to),
      (toContainer = List<ContainedTypeAndId>()) =>
        to.index !== null
          ? toContainer.insert(to.index, getContainedTypeAndIdOnly(entry))
          : toContainer.push(getContainedTypeAndIdOnly(entry)),
    );
}
